import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button,
  List, ListItem, ListItemText, IconButton, Chip,
  CircularProgress, Alert, Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import appConfig from '../../../utils/appConfig';

export interface SystemSettings {
  currentYear: string;
  stations: string[];
  paymentAmounts: string[];
}

interface Props {
  adminToken: string;
  onSnackbar: (msg: string, sev: 'success' | 'error') => void;
  onSettingsChange?: (settings: SystemSettings) => void;
}

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const AdminSettings: React.FC<Props> = ({ adminToken, onSnackbar, onSettingsChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [currentYear, setCurrentYear] = useState('');
  const [stations, setStations] = useState<string[]>([]);
  const [newStation, setNewStation] = useState('');
  const [paymentAmounts, setPaymentAmounts] = useState<string[]>([]);
  const [newAmount, setNewAmount] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${appConfig.apiUrls.baseUrl}/settings`, { headers: authHeaders(adminToken) });
      const data = await res.json();
      if (res.ok) {
        setCurrentYear(data.currentYear || '');
        setStations(data.stations || []);
        setPaymentAmounts(data.paymentAmounts || []);
        onSettingsChange?.(data);
      } else {
        setError(data.error || 'שגיאה בטעינת הגדרות');
      }
    } catch {
      setError('שגיאת תקשורת');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSection = async (section: 'year' | 'stations' | 'amounts') => {
    setSaving(section);
    try {
      const payload: Partial<SystemSettings> = {};
      if (section === 'year') payload.currentYear = currentYear;
      if (section === 'stations') payload.stations = stations;
      if (section === 'amounts') payload.paymentAmounts = paymentAmounts;

      const res = await fetch(`${appConfig.apiUrls.baseUrl}/settings`, {
        method: 'PUT',
        headers: authHeaders(adminToken),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        onSnackbar('ההגדרות נשמרו בהצלחה', 'success');
        onSettingsChange?.(data.settings);
      } else {
        onSnackbar(data.error || 'שגיאה בשמירה', 'error');
      }
    } catch {
      onSnackbar('שגיאת תקשורת', 'error');
    } finally {
      setSaving(null);
    }
  };

  const addStation = () => {
    const val = newStation.trim();
    if (!val || stations.includes(val)) return;
    setStations(prev => [...prev, val]);
    setNewStation('');
  };

  const removeStation = (idx: number) => setStations(prev => prev.filter((_, i) => i !== idx));

  const moveStation = (idx: number, dir: -1 | 1) => {
    const next = [...stations];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setStations(next);
  };

  const addAmount = () => {
    const val = newAmount.trim();
    if (!val || paymentAmounts.includes(val)) return;
    setPaymentAmounts(prev => [...prev, val].sort((a, b) => Number(b) - Number(a)));
    setNewAmount('');
  };

  const removeAmount = (idx: number) => setPaymentAmounts(prev => prev.filter((_, i) => i !== idx));

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>הגדרות מערכת</Typography>

      {/* Section 1: Current Year */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          תקופה נוכחית
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          ערך זה ישמש בעת יצירת שיבוץ חדש ובחיפוש שיבוצים קיימים בזוהו.
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small" label="תקופה (לדוגמא: תשפו)" value={currentYear}
            onChange={e => setCurrentYear(e.target.value)}
            inputProps={{ style: { textAlign: 'right', direction: 'rtl' } }}
            sx={{ width: 220 }}
          />
          <Button variant="contained" startIcon={saving === 'year' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => saveSection('year')} disabled={saving === 'year' || !currentYear.trim()}>
            שמור
          </Button>
          <Chip label={`תקופה פעילה: ${currentYear}`} color="primary" variant="outlined" />
        </Box>
      </Paper>

      {/* Section 2: Stations */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            תחנות עלייה ({stations.length})
          </Typography>
          <Button variant="contained" size="small"
            startIcon={saving === 'stations' ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={() => saveSection('stations')} disabled={saving === 'stations'}>
            שמור שינויים
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          רשימת התחנות שתוצג בעת בחירת תחנת עלייה לתלמיד.
        </Typography>

        {/* Add station */}
        <Box display="flex" gap={1} mb={2}>
          <TextField size="small" label="תחנה חדשה" value={newStation}
            onChange={e => setNewStation(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addStation()}
            inputProps={{ style: { textAlign: 'right' } }}
            sx={{ flex: 1, maxWidth: 400 }} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addStation}
            disabled={!newStation.trim() || stations.includes(newStation.trim())}>
            הוסף
          </Button>
        </Box>

        <List dense sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
          {stations.map((s, idx) => (
            <ListItem
              key={idx}
              sx={{ borderBottom: idx < stations.length - 1 ? '1px solid #f0f0f0' : 'none', py: 0.5 }}
              secondaryAction={
                <Box display="flex" gap={0.5}>
                  <IconButton size="small" onClick={() => moveStation(idx, -1)} disabled={idx === 0} title="הזז למעלה">▲</IconButton>
                  <IconButton size="small" onClick={() => moveStation(idx, 1)} disabled={idx === stations.length - 1} title="הזז למטה">▼</IconButton>
                  <IconButton size="small" color="error" onClick={() => removeStation(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={<Typography variant="body2">{idx + 1}. {s}</Typography>}
              />
            </ListItem>
          ))}
          {stations.length === 0 && (
            <ListItem><ListItemText primary={<Typography color="text.secondary">אין תחנות מוגדרות</Typography>} /></ListItem>
          )}
        </List>
      </Paper>

      {/* Section 3: Payment Amounts */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            סכומי תשלום אפשריים
          </Typography>
          <Button variant="contained" size="small"
            startIcon={saving === 'amounts' ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={() => saveSection('amounts')} disabled={saving === 'amounts'}>
            שמור שינויים
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          הסכומים שיוצגו כאפשרויות בעת עריכת סכום לתשלום לתלמיד.
        </Typography>

        <Box display="flex" gap={1} mb={2} alignItems="center">
          <TextField size="small" label="סכום חדש (₪)" value={newAmount} type="number"
            onChange={e => setNewAmount(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addAmount()}
            inputProps={{ style: { textAlign: 'right' }, min: 0 }}
            sx={{ width: 180 }} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addAmount}
            disabled={!newAmount.trim() || paymentAmounts.includes(newAmount.trim())}>
            הוסף
          </Button>
        </Box>

        <Grid container spacing={1}>
          {paymentAmounts.map((a, idx) => (
            <Grid item key={idx}>
              <Chip
                label={`${Number(a).toLocaleString()} ₪`}
                onDelete={() => removeAmount(idx)}
                color={a === '0' ? 'default' : 'primary'}
                variant="outlined"
              />
            </Grid>
          ))}
          {paymentAmounts.length === 0 && (
            <Grid item><Typography color="text.secondary">אין סכומים מוגדרים</Typography></Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default AdminSettings;
export type { SystemSettings as SystemSettingsType };
